import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TimeOutLocalStorageService } from '@app/core/services/timeOutLocalStorage.service';
import { SecureHttp } from '@app/security/secure-http';
import { markSessionExpired } from '@app/security/session.service';
import { interval, map, Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { Title } from '@angular/platform-browser';

@Component({
    selector: 'rn-timer-popup',
    templateUrl: './timer-popup.component.html',
    styleUrls: ['./timer-popup.component.scss'],
    standalone: true
})
export class TimerPopupComponent implements OnInit, OnDestroy {
  @ViewChild('audioControl', { static: true }) audiblePing: ElementRef;

  showWarning = false;
  timeLeftInSeconds: number;
  chimeHasPlayed = false;
  subscription = null;
  // DIS-1789: the timeout emitter is static, so a subscription left behind by a
  // destroyed popup keeps reacting (and now marks the tab) forever.
  private timeOutEventSubscription: Subscription | null = null;

  private readonly warningTimeInMinutes = 5 * 60;
  constructor(
    private timeOutLocalStorageService: TimeOutLocalStorageService,
    private secureHttp: SecureHttp,
    private router: Router,
    private title: Title,
  ) {}

  ngOnDestroy() {
    this.teardown();
  }

  private teardown() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.timeOutEventSubscription?.unsubscribe();
  }

  ngOnInit() {
    // Running ngOnInit again must not orphan the previous subscriptions.
    this.teardown();
    this.timeOutEventSubscription =
      TimeOutLocalStorageService.timeOutEventChange.subscribe(({ type }) => {
        if (type === 'delete') {
          // Another tab logged out or timed out. This tab didn't ask to, so it
          // waits on the login page rather than opening its own Auth0 login
          // (DIS-1789).
          markSessionExpired();
          this.router.navigateByUrl('/logout');
        }
      });
    this.timeOutLocalStorageService.listenToTimeoutChanges();

    this.subscription = interval(1000)
      .pipe(
        map(() => {
          const currentTime = new Date().getTime();

          const expirationTime =
            this.timeOutLocalStorageService.getTimeoutValue()
              ? new Date(
                  JSON.parse(this.timeOutLocalStorageService.getTimeoutValue()),
                )
              : null;

          if (!expirationTime) {
            return false;
          }

          const timeLeft = expirationTime.getTime() - currentTime;
          this.timeLeftInSeconds = Math.floor(timeLeft / 1000);

          return timeLeft <= this.warningTimeInMinutes * 1000; // in milliseconds
        }),
      )
      .subscribe((showWarning) => {
        this.showWarning = showWarning;
        if (this.timeLeftInSeconds <= 0) {
          // Idle timeout: the user is very likely not at the keyboard, so the
          // login page waits for them instead of starting Auth0 (DIS-1789).
          markSessionExpired();
          this.logOut();
        }
        if (showWarning) {
          this.title.setTitle('Session is expiring!');
          if (!this.chimeHasPlayed) {
            this.audiblePing.nativeElement.play();
            this.chimeHasPlayed = true;
          }
        } else {
          this.title.setTitle('Client Portal');
        }
      });
  }

  get minutesLeft() {
    return Math.floor(this.timeLeftInSeconds / 60);
  }
  get secondsLeft() {
    return (this.timeLeftInSeconds % 60).toString().padStart(2, '0');
  }

  stayLoggedIn() {
    const url = `${environment.portalURLBase}/refresh-session`;
    this.secureHttp.get(url, {}, true).subscribe();
    this.chimeHasPlayed = false;
  }

  logOut() {
    this.router.navigateByUrl('/logout');
  }
}
